import { convert, encodings } from "./converter.js";
import registry from "../dist/font-mappings.json" with { type: "json" };
// Text fields always retain their source text. Only the rendered specimen is mapped.
// Profiles are independent of UI state and can be overridden per font.
const compiled = new Map();
// BG Courier and BG Literaturuli use the 33 modern letters consecutively,
// without the historical-letter gaps in STD8. Both glyph charts were verified.
const bgAlphabet = "აბგდევზთიკლმნოპჟრსტუფქღყშჩცძწჭხჯჰ";
export function normalizeGeorgian(char) {
  const n = char.codePointAt(0);
  if (n >= 0x1c90 && n <= 0x1cbf) return String.fromCodePoint(n - 0xbc0);
  if (n >= 0x2d00 && n <= 0x2d25) return String.fromCodePoint(n - 0x1c30);
  if (n >= 0x10a0 && n <= 0x10c5) return String.fromCodePoint(n + 0x30);
  return char;
}
export function resolvePreviewMapping(font, override) {
  if (override === "raw")
    return { table: "raw", layout: "unchanged", label: "Original text" };
  if (override && encodings.includes(override))
    return { table: override, layout: "unchanged", label: override };
  const entry = registry.fonts[String(font.id)];
  if (entry?.map) return {table:"per-font", charMap:entry.map, cacheKey:String(font.id) + ':' + entry.sha256, label:entry.name, status:entry.status, missing:entry.missing};
  if (entry?.profile) return {...entry.profile, status:entry.status};
  if (
    font.previewMapping?.table &&
    encodings.includes(font.previewMapping.table)
  )
    return {
      ...font.previewMapping,
      label: font.previewMapping.label || font.previewMapping.table,
    };
  const encoding = font.encoding || "",
    layout = font.layout || "",
    writings = font.writings || [];
  if (encoding.startsWith("Unicode")) {
    let table = "_utf8_";
    if (!font.coverage?.includes(0x10d0)) {
      if (writings.includes("Khutsuri Nuskhuri")) table = "nuskhuri";
      else if (writings.includes("Khutsuri Mtavruli"))
        table = "_utf8_UpperCase_";
      else if (font.coverage?.includes(0x1c90)) table = "modern-mtavruli";
    }
    return {
      table,
      layout: "unchanged",
      label:
        table === "_utf8_"
          ? "Unicode"
          : table === "nuskhuri"
            ? "Unicode Nuskhuri"
            : table === "modern-mtavruli"
              ? "Unicode Mtavruli"
              : "Unicode Asomtavruli",
    };
  }
  if (encoding.startsWith("STD8"))
    return { table: "_standart_", layout: "unchanged", label: "STD8" };
  if (encoding === "Latin") {
    if (layout.startsWith("Russian"))
      return {
        table: "_eng_",
        layout: "eng-to-rus",
        label: "Legacy Latin · Russian keyboard",
      };
    if (layout.startsWith("Typewriter"))
      return {
        table: /Kolkhety TeleType/i.test(font.name)
          ? "KolkhetiTeleType"
          : "Academic",
        layout: "unchanged",
        label: "Legacy typewriter",
      };
    return {
      table: "_eng_",
      layout: "unchanged",
      label: "Legacy Latin · QWERTY",
    };
  }
  return {
    table: "raw",
    layout: "unchanged",
    label: "Unknown mapping",
    uncertain: true,
  };
}
function legacyMap(profile) {
  const key = profile.cacheKey || profile.table + "|" + (profile.layout || "unchanged");
  if (!compiled.has(key)) {
    const map = new Map();
    if (profile.charMap) {
      const direct = new Map(Object.entries(profile.charMap));
      compiled.set(key, direct);
      return direct;
    }
    if (profile.table.startsWith("av-academy")) {
      const bold = profile.table === "av-academy-bold";
      const slots = bold ? "f,ultdpsbrkvyjg;hcneamq'ixw.oz[]/" : "f,ultdpsbrkvyjg;hcneamq.ixw'oz[]/";
      [...bgAlphabet].forEach((char, i) => map.set(char, slots[i]));
      // This keyboard font also repurposes punctuation and digit positions.
      Object.entries(bold ? {",":"=", ".":"+", ":":"|", ";":"\\", "?":"~"} : {",":"7", ".":"8", ":":"5", ";":"9", "!":"=", "?":"0", "(":"~", ")":"`", "%":"+", "0":"_", "1":"!", "2":"@", "3":"#", "4":"$", "5":"%", "6":"^", "7":"*", "8":"(", "9":")"}).forEach(([char, slot]) => map.set(char, slot));
      compiled.set(key, map);
      return map;
    }
    if (profile.table === "bg-modern") {
      [...bgAlphabet].forEach((char, i) => map.set(char, String.fromCodePoint(0xc0 + i)));
      compiled.set(key, map);
      return map;
    }
    for (let cp = 0x10d0; cp <= 0x10ff; cp++) {
      const ch = String.fromCodePoint(cp);
      map.set(
        ch,
        convert(ch, "_utf8_", profile.table, profile.layout || "unchanged"),
      );
    }
    compiled.set(key, map);
  }
  return compiled.get(key);
}
export function mapPreviewText(text, font, { enabled = true, mapping } = {}) {
  if (typeof text !== "string")
    throw new TypeError("Preview text must be a string");
  if (!enabled || mapping === "raw") return text;
  const profile = resolvePreviewMapping(font, mapping);
  if (profile.table === "raw") return text;
  const unicode = profile.table === "_utf8_";
  const map = legacyMap(profile);
  return [...text]
    .map((char) => {
      const normalized = normalizeGeorgian(char),
        cp = normalized.codePointAt(0);
      if (cp < 0x10d0 || cp > 0x10ff)
        return profile.charMap || profile.table.startsWith("av-academy") ? map.get(char) || char : char;
      if (unicode) {
        if (char === normalized || font.coverage?.includes(char.codePointAt(0)))
          return char;
        return normalized;
      }
      return map.get(normalized) || char;
    })
    .join("");
}
